import type { VisitorLocale } from '@cloudsign/shared';
import { VISITOR_LOCALES, VISITOR_LOCALE_INFO, isLocaleAllowed } from '@cloudsign/shared';
import { ApiClientError } from '../../lib/api';

export const ONBOARDING = '/onboarding';

/**
 * Whether this portal may offer visitor languages that are still drafts. The server decides what visitors really get
 * (platform setting `i18n.allowDraftLocales`, off on live, on for the test server); no route tells a customer admin that
 * setting, so the portal build says it. Set VITE_ALLOW_DRAFT_LOCALES=1 for the test server build.
 */
export function allowDraftLocales(): boolean {
  return import.meta.env['VITE_ALLOW_DRAFT_LOCALES'] === '1';
}

export interface LocaleChoice {
  code: VisitorLocale;
  endonym: string;
  draft: boolean;
  /** Draft and drafts not allowed: shown (because it is already saved) but cannot be newly chosen. */
  locked: boolean;
}

/** The languages to list. Drafts appear only where the platform allows them, or when a saved value already uses one. */
export function localeChoices(saved: readonly string[], allowDrafts = allowDraftLocales()): LocaleChoice[] {
  return VISITOR_LOCALES.filter((code) => isLocaleAllowed(code, allowDrafts) || saved.includes(code)).map((code) => ({
    code,
    endonym: VISITOR_LOCALE_INFO[code].endonym,
    draft: !isLocaleAllowed(code, false),
    locked: !isLocaleAllowed(code, allowDrafts),
  }));
}

export function isConflict(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 409;
}

/** "123456789" -> "123 456 789", easy to read out and type. */
export function spacedCode(code: string): string {
  return code.replace(/(\d{3})(?=\d)/g, '$1 ');
}
